/**
 * StatusBadge Component
 * Consistent status indicators across the dashboard
 * Based on LifeScribe Design System v4.0
 */

import React from 'react';
import { Chip, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors, typography } from '../../theme/designTokens';
import { icons, statusIcons } from '../../theme/icons';

export type StatusType =
  | 'active'
  | 'inactive'
  | 'pending'
  | 'error'
  | 'success'
  | 'warning'
  | 'verified'
  | 'unverified';

export type StatusVariant = 'filled' | 'outlined' | 'dot';
export type StatusSize = 'sm' | 'md' | 'lg';

export interface StatusBadgeProps {
  status: StatusType;
  variant?: StatusVariant;
  size?: StatusSize;
  icon?: boolean;
  label?: string;
  customColor?: string;
}

// Status color mappings
const statusColors = {
  active: {
    main: colors.success.main,
    light: colors.success.light,
    bg: colors.success[50],
    text: colors.success.dark,
  },
  inactive: {
    main: colors.gray[500],
    light: colors.gray[400],
    bg: colors.gray[100],
    text: colors.gray[700],
  },
  pending: {
    main: colors.warning.main,
    light: colors.warning.light,
    bg: colors.warning[50],
    text: colors.warning.dark,
  },
  error: {
    main: colors.error.main,
    light: colors.error.light,
    bg: colors.error[50],
    text: colors.error.dark,
  },
  success: {
    main: colors.success.main,
    light: colors.success.light,
    bg: colors.success[50],
    text: colors.success.dark,
  },
  warning: {
    main: colors.warning.main,
    light: colors.warning.light,
    bg: colors.warning[50],
    text: colors.warning.dark,
  },
  verified: {
    main: colors.success.main,
    light: colors.success.light,
    bg: colors.success[50],
    text: colors.success.dark,
  },
  unverified: {
    main: colors.gray[500],
    light: colors.gray[400],
    bg: colors.gray[100],
    text: colors.gray[700],
  },
};

// Status icon mappings
const getStatusIcon = (status: StatusType): string => {
  const iconMap: Record<StatusType, string> = {
    active: statusIcons.active,
    inactive: statusIcons.inactive,
    pending: statusIcons.pending,
    error: statusIcons.error,
    success: statusIcons.success,
    warning: statusIcons.warning,
    verified: statusIcons.verified,
    unverified: statusIcons.unverified,
  };
  return iconMap[status];
};

// Status label mappings
const getStatusLabel = (status: StatusType): string => {
  const labelMap: Record<StatusType, string> = {
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    verified: 'Verified',
    unverified: 'Unverified',
  };
  return labelMap[status];
};

// Size configurations
const sizeConfig = {
  sm: {
    height: 20,
    fontSize: typography.sizes.xs,
    dotSize: 6,
    iconSize: 14,
    padding: '2px 8px',
  },
  md: {
    height: 24,
    fontSize: typography.sizes.sm,
    dotSize: 8,
    iconSize: 16,
    padding: '4px 12px',
  },
  lg: {
    height: 28,
    fontSize: typography.sizes.base,
    dotSize: 10,
    iconSize: 18,
    padding: '6px 16px',
  },
};

const StyledChip = styled(Chip)<{
  statusType: StatusType;
  statusVariant: StatusVariant;
  statusSize: StatusSize;
}>(({ statusType, statusVariant, statusSize }) => {
  // Fallback to 'inactive' colors if status type not found
  const colorScheme = statusColors[statusType] || statusColors.inactive;
  const size = sizeConfig[statusSize] || sizeConfig.md;

  const baseStyles = {
    height: size.height,
    fontSize: size.fontSize,
    fontWeight: typography.weights.medium,
    borderRadius: '9999px',
    transition: 'all 150ms ease-in-out',
  };

  if (statusVariant === 'filled') {
    return {
      ...baseStyles,
      backgroundColor: colorScheme.bg,
      color: colorScheme.text,
      border: `1px solid ${colorScheme.light}`,
      '&:hover': {
        backgroundColor: colorScheme.light,
      },
    };
  }

  if (statusVariant === 'outlined') {
    return {
      ...baseStyles,
      backgroundColor: 'transparent',
      color: colorScheme.main,
      border: `2px solid ${colorScheme.main}`,
      '&:hover': {
        backgroundColor: colorScheme.bg,
      },
    };
  }

  return baseStyles;
});

const DotContainer = styled(Box)<{ statusType: StatusType; statusSize: StatusSize }>(
  ({ statusType, statusSize }) => {
    const colorScheme = statusColors[statusType];
    const size = sizeConfig[statusSize];

    return {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      '& .status-dot': {
        width: size.dotSize,
        height: size.dotSize,
        borderRadius: '50%',
        backgroundColor: colorScheme.main,
        flexShrink: 0,
      },
      '& .status-label': {
        fontSize: size.fontSize,
        fontWeight: typography.weights.medium,
        color: colorScheme.text,
      },
    };
  }
);

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  variant = 'filled',
  size = 'md',
  icon = false,
  label,
  customColor,
}) => {
  const displayLabel = label || getStatusLabel(status);
  const iconName = getStatusIcon(status);

  // Dot variant
  if (variant === 'dot') {
    return (
      <DotContainer statusType={status} statusSize={size}>
        <span className="status-dot" />
        <span className="status-label">{displayLabel}</span>
      </DotContainer>
    );
  }

  // Filled or Outlined variant (using MUI Chip)
  return (
    <StyledChip
      statusType={status}
      statusVariant={variant}
      statusSize={size}
      label={displayLabel}
      icon={
        icon ? (
          <span
            className="material-icons"
            style={{
              fontSize: sizeConfig[size].iconSize,
              color: 'inherit',
            }}
          >
            {iconName}
          </span>
        ) : undefined
      }
    />
  );
};

export default StatusBadge;
